import { useEffect, useRef, useState } from "react";
import RightTimeApi from "@/API";
import { toast } from "react-toastify";
import Enums from "@/enums";
import { prefetchWhenIdle } from "@/utils/runWhenIdle";
import SiteTestAccessibilityTable from "../elements/tables/SiteTestAccessibility";
import SiteRoleAccessibility from "../elements/tables/SiteRoleAccessibility";
import SiteJobsTable from "../elements/tables/SiteJobsTable";
import SiteListTable from "../elements/tables/SiteListTable";

export default function CompanySitesTab(props: any) {
  const { company, updateSpinner, activeTab, adminPerms } = props.tabData;
  const canEditSites = adminPerms?.includes(Enums.ROLE_PERMS.EditSite)
    || adminPerms?.includes(Enums.ROLE_PERMS.CompanyAdmin)
    || adminPerms?.includes(Enums.ROLE_PERMS.SystemAdmin);

  const [siteData, updateSiteData] = useState<any>(null);
  const [activeSite, updateActiveSite] = useState<any>(null);
  const [contextLoaded, setContextLoaded] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const validationRequestRef = useRef(0);
  const contextRequestRef = useRef(0);

  useEffect(() => {
    if (isOnThisTab())
      loadInitialAsync();
  }, [activeTab]);

  function isOnThisTab() {
    return props && activeTab && activeTab === "sites";
  }

  if (!isOnThisTab()) return null;

  async function prefetchContextAsync() {
    if (contextLoaded || !company?.id) return;
    const requestId = ++contextRequestRef.current;
    try {
      const resp = await RightTimeApi.getAsync(`/CompanySitesTab/${company.id}/context`);
      if (requestId !== contextRequestRef.current || !resp.ok) return;
      updateSiteData((prev: any) => ({
        ...prev,
        ...resp.jsonData,
        sites: prev.sites,
        subEntityTypeName: prev.subEntityTypeName,
        permissions: prev.permissions,
      }));
      setContextLoaded(true);
    } catch (error) {
      console.error("Error prefetching sites context:", error);
    }
  }

  async function loadValidationAsync() {
    if (!company?.id) return;
    const requestId = ++validationRequestRef.current;
    try {
      const resp = await RightTimeApi.getAsync(`/CompanySitesTab/${company.id}/validation`);
      if (requestId !== validationRequestRef.current || !resp.ok) return;
      const messagesBySiteId = resp.jsonData?.messagesBySiteId ?? {};
      updateSiteData((prev: any) => ({
        ...prev,
        sites: (prev.sites ?? []).map((site: any) => ({
          ...site,
          messages: messagesBySiteId[site.id] ?? [],
        })),
      }));
      updateActiveSite((current: any) => {
        if (!current?.id) return current;
        const messages = messagesBySiteId[current.id];
        if (!messages) return current;
        return { ...current, messages };
      });
    } catch (error) {
      console.error("Error fetching site validation:", error);
    }
  }

  async function loadInitialAsync() {
    updateSpinner(true);
    const resp = await RightTimeApi.getAsync(`/CompanySitesTab/${company.id}/list`);
    updateSpinner(false);

    if (resp?.ok && resp.jsonData?.sites) {
      updateSiteData(resp.jsonData);
      updateActiveSite(null);
      setContextLoaded(false);
      loadValidationAsync();
      prefetchWhenIdle(() => prefetchContextAsync());
    }
  }

  async function getCompanySitesAsync() {
    updateSpinner(true);
    const selectedId = activeSite?.id ?? null;
    const resp = await RightTimeApi.getAsync("/CompanySitesTab/" + company.id);
    updateSpinner(false);

    if (resp?.ok && resp.jsonData?.sites) {
      updateSiteData(resp.jsonData);
      setContextLoaded(true);

      if (selectedId) {
        const existingSite = resp.jsonData.sites.find((s: any) => s.id === selectedId);
        updateActiveSite(existingSite ?? null);
      } else {
        updateActiveSite(null);
      }
    }
  }

  async function selectActiveSite(site: any) {
    updateActiveSite(site);
    if (!site?.id || contextLoaded) return;
    setDetailLoading(true);
    try {
      await prefetchContextAsync();
    } finally {
      setDetailLoading(false);
    }
  }

  function setActiveSite(e: any) {
    const id = parseInt(e.currentTarget.getAttribute("data-id"));
    const site = siteData.sites.find((u: any) => u.id === id);
    if (site) {
      selectActiveSite(site);
    }
  }

  async function toggleSiteTestAsync(companyTestTypeId) {
    if (!canEditSites) return;
    let data = {
      siteId: activeSite.id,
      companyTestTypeId: companyTestTypeId,
    };
    const resp = await RightTimeApi.putAsync("/SiteTestActiveToggle", data);
    if (resp && resp.ok) {
      let site = siteData.sites.find((s) => s.id === activeSite.id);
      if (site) {
        if (resp.jsonData.active)
          toast.success(
            "Test '" + resp.jsonData.testName + "' activated for " + site.name
          );
        else
          toast.success(
            "Test '" +
              resp.jsonData.testName +
              "' de-activated for " +
              site.name
          );
      } else {
        if (resp.jsonData.active)
          toast.success("Test '" + resp.jsonData.testName + "' activated");
        else
          toast.success("Test '" + resp.jsonData.testName + "' de-activated");
      }
      getCompanySitesAsync();
    }
  }

  async function toggleSiteRoleAsync(roleId) {
    if (!canEditSites) return;
    updateSpinner(true);
    const resp = await RightTimeApi.putAsync(
      `/CompanyRole/${roleId}/Site/${activeSite.id}/Toggle`
    );
    updateSpinner(false);
    if (resp && resp.ok) {
      toast.success("Role site link updated");
      getCompanySitesAsync();
    } else {
      toast.error("Failed to update role site link.");
    }
  }

  if (siteData) {
    return (
      <div className="companySitesTab">
        <SiteListTable
          siteData={siteData}
          activeSite={activeSite}
          updateActiveSite={selectActiveSite}
          updateData={getCompanySitesAsync}
          companyId={company.id}
          adminPerms={adminPerms}
          company={company}
        />
        {detailLoading && (
          <div className="cttDetailLoading">Loading site details…</div>
        )}
        {activeSite && !detailLoading && contextLoaded && (
        <SiteTestAccessibilityTable
          activeSite={activeSite}
          companyTestTypes={siteData?.companyTestTypes}
          toggleSiteTest={toggleSiteTestAsync}
          siteTestTypes={siteData?.siteTestTypes}
          subEntityTypeName={siteData?.subEntityTypeName}
          adminPerms={adminPerms}
        />)}
        {activeSite && !detailLoading && contextLoaded && (
        <SiteRoleAccessibility
          roles={siteData?.roles}
          activeSite={activeSite}
          roleSites={siteData?.roleSites}
          toggleSiteRole={toggleSiteRoleAsync}
          subEntityTypeName={siteData?.subEntityTypeName}
          adminPerms={adminPerms}
        />)}
        {activeSite && !detailLoading && contextLoaded && (
        <SiteJobsTable
          siteJobs={siteData?.siteJobs}
          jobs={siteData?.jobs}
          activeSite={activeSite}
          getCompanySites={getCompanySitesAsync}
          siteData={siteData}
          subEntityTypeName={siteData?.subEntityTypeName}
          adminPerms={adminPerms}
          departmentsEnabled={company?.departmentsEnabled}
        />)}
      </div>
    );
  } else return null;
}
